import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import {
  SUPPLIER_INVOICE_ALLOCATION_TARGETS,
  allocationTargetRequiresId,
  distributeInvoiceAmountsByAllocation,
  formatCentsAsDecimal,
  parseMoneyToCents,
} from "@shared/finance/supplierInvoices";
import type { SupplierInvoiceAllocationTarget } from "@shared/finance/supplierInvoices";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatInstallationReference, installationMatchesSearchQuery } from "@shared/finance/manualExpenses";
import { ChevronsUpDown, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface Allocation {
  id: number;
  targetType: string;
  targetId?: number | null;
  allocatedAmount: string;
  allocatedPercentage?: string | null;
  notes?: string | null;
  installationCode?: string | null;
  clientId?: string | null;
  clientName?: string | null;
  siteName?: string | null;
  workDescription?: string | null;
  humanReference?: string | null;
}

interface SupplierInvoiceAllocationsProps {
  invoiceId: number;
  allocations: Allocation[];
  canWrite: boolean;
  totalAmount: string;
  taxableBase: string;
  vatAmount: string;
}

const TARGET_LABELS: Record<SupplierInvoiceAllocationTarget, string> = {
  installation: "Instalación",
  warehouse: "Almacén",
  general_overhead: "Gastos generales",
  unallocated: "Sin imputar",
};

export function normalizeAllocationAmount(value: string | number) {
  const cents = parseMoneyToCents(value);
  return cents === null ? String(value) : formatCentsAsDecimal(cents);
}

function formatEUR(amount: string | number) {
  const n = typeof amount === "string" ? parseFloat(amount) : amount;
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);
}

const EMPTY_FORM = {
  targetType: "unallocated" as SupplierInvoiceAllocationTarget,
  targetId: "",
  allocatedAmount: "0.00",
  allocatedPercentage: "",
  notes: "",
};

export function SupplierInvoiceAllocations({ invoiceId, allocations, canWrite, totalAmount, taxableBase, vatAmount }: SupplierInvoiceAllocationsProps) {
  const utils = trpc.useUtils();
  const [setEditorOpen, setSetEditorOpen] = useState(false);
  const [setRows, setSetRows] = useState<Array<{ targetType: SupplierInvoiceAllocationTarget; targetId: string; allocatedAmount: string; notes: string }>>([]);
  const replaceAllocations = trpc.supplierInvoices.replaceAllocations.useMutation({
    onSuccess: () => { toast.success("Reparto guardado"); utils.supplierInvoices.getById.invalidate({ invoiceId }); setSetEditorOpen(false); },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [installationComboOpen, setInstallationComboOpen] = useState(false);

  const installationsQuery = trpc.installations.list.useQuery(undefined, { enabled: dialogOpen || setEditorOpen });
  const selectedInstallation = (installationsQuery.data ?? []).find((inst: any) => String(inst.id) === form.targetId);

  const createAllocation = trpc.supplierInvoices.createAllocation.useMutation({
    onSuccess: () => {
      toast.success("Imputación añadida");
      utils.supplierInvoices.getById.invalidate({ invoiceId });
      setDialogOpen(false);
    },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  const updateAllocation = trpc.supplierInvoices.updateAllocation.useMutation({
    onSuccess: () => {
      toast.success("Imputación actualizada");
      utils.supplierInvoices.getById.invalidate({ invoiceId });
      setDialogOpen(false);
      setEditingId(null);
    },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  const deleteAllocation = trpc.supplierInvoices.deleteAllocation.useMutation({
    onSuccess: () => {
      toast.success("Imputación eliminada");
      utils.supplierInvoices.getById.invalidate({ invoiceId });
    },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  function openAdd() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }

  function openEdit(a: Allocation) {
    setEditingId(a.id);
    setForm({
      targetType: a.targetType as SupplierInvoiceAllocationTarget,
      targetId: a.targetId ? String(a.targetId) : "",
      allocatedAmount: normalizeAllocationAmount(a.allocatedAmount),
      allocatedPercentage: a.allocatedPercentage ?? "",
      notes: a.notes ?? "",
    });
    setDialogOpen(true);
  }

  function buildPayload() {
    return {
      targetType: form.targetType,
      targetId: allocationTargetRequiresId(form.targetType) && form.targetId
        ? parseInt(form.targetId, 10)
        : null,
      allocatedAmount: form.allocatedAmount,
      allocatedPercentage: form.allocatedPercentage || null,
      notes: form.notes || null,
    };
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const allocation = buildPayload();
    if (editingId !== null) {
      updateAllocation.mutate({ allocationId: editingId, allocation });
    } else {
      createAllocation.mutate({ invoiceId, allocation });
    }
  }

  const isPending = createAllocation.isPending || updateAllocation.isPending;
  const needsId = allocationTargetRequiresId(form.targetType);
  const distributed = distributeInvoiceAmountsByAllocation(taxableBase, vatAmount, allocations);
  const editedTotalCents = setRows.reduce((sum, row) => sum + (parseMoneyToCents(row.allocatedAmount) ?? 0), 0);
  const invoiceTotalCents = parseMoneyToCents(totalAmount) ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {allocations.length} {allocations.length === 1 ? "imputación" : "imputaciones"}
        </span>
        {canWrite && (
          <Button size="sm" onClick={() => { setSetRows(allocations.map((a) => ({ targetType: a.targetType as SupplierInvoiceAllocationTarget, targetId: String(a.targetId ?? ""), allocatedAmount: normalizeAllocationAmount(a.allocatedAmount), notes: a.notes ?? "" }))); setSetEditorOpen(true); }}>
            Editar reparto completo
          </Button>
        )}
      </div>

      {allocations.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">Sin imputaciones registradas.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tipo</TableHead>
                <TableHead>Imputación / Destino</TableHead>
                <TableHead className="text-right">Base</TableHead>
                <TableHead className="text-right">IVA</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">%</TableHead>
                <TableHead>Notas</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allocations.map((a, i) => (
                <TableRow key={a.id}>
                  <TableCell>
                    <Badge variant="outline">
                      {TARGET_LABELS[a.targetType as SupplierInvoiceAllocationTarget] ?? a.targetType}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-xs truncate font-medium">
                    {a.humanReference
                      ? a.humanReference
                      : a.targetType === "installation" && a.targetId
                      ? formatInstallationReference(a)
                      : a.targetType === "warehouse"
                      ? "Almacén"
                      : a.targetType === "general_overhead"
                      ? "Gastos generales"
                      : "Sin imputar"}
                  </TableCell>
                  <TableCell className="text-right">{formatEUR(distributed[i]?.allocatedTaxableBase ?? "0")}</TableCell>
                  <TableCell className="text-right">{formatEUR(distributed[i]?.allocatedVatAmount ?? "0")}</TableCell>
                  <TableCell className="text-right font-medium">{formatEUR(a.allocatedAmount)}</TableCell>
                  <TableCell className="text-right">{a.allocatedPercentage ? `${a.allocatedPercentage}%` : "-"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{a.notes ?? "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={setEditorOpen} onOpenChange={setSetEditorOpen}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100%-1rem)] max-w-3xl overflow-y-auto p-4 sm:w-full sm:p-6">
          <DialogHeader><DialogTitle>Editar reparto completo de la factura</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">La base y el IVA se asignan proporcionalmente al total de cada destino. Los céntimos residuales se distribuyen por mayor resto para conservar exactamente ambos totales.</p>
          <div className="grid max-h-[45dvh] grid-cols-1 gap-3 overflow-y-auto pr-1 sm:max-h-[55vh]">
            {setRows.map((row, index) => (
              <div className="grid min-w-0 grid-cols-1 gap-3 rounded-md border p-3 sm:grid-cols-2 sm:items-end" key={index}>
                <div className="min-w-0"><Label>Tipo de destino</Label><Select value={row.targetType} onValueChange={(v) => setSetRows((rows) => rows.map((r, i) => i === index ? { ...r, targetType: v as SupplierInvoiceAllocationTarget, targetId: "" } : r))}><SelectTrigger className="w-full min-w-0"><SelectValue /></SelectTrigger><SelectContent>{SUPPLIER_INVOICE_ALLOCATION_TARGETS.map((t) => <SelectItem key={t} value={t}>{TARGET_LABELS[t]}</SelectItem>)}</SelectContent></Select></div>
                {row.targetType === "installation" ? <div className="min-w-0"><Label>Obra / expediente</Label><Select value={row.targetId} onValueChange={(v) => setSetRows((rows) => rows.map((r, i) => i === index ? { ...r, targetId: v } : r))}><SelectTrigger className="w-full min-w-0"><SelectValue placeholder="Seleccionar obra" /></SelectTrigger><SelectContent className="w-[var(--radix-select-trigger-width)] min-w-0 max-w-[calc(100vw-2rem)]">{(installationsQuery.data ?? []).map((inst: any) => <SelectItem className="min-w-0" key={inst.id} value={String(inst.id)}><span className="block min-w-0 truncate" title={formatInstallationReference(inst)}>{formatInstallationReference(inst)}</span></SelectItem>)}</SelectContent></Select></div> : <div className="text-sm text-muted-foreground pb-2">{TARGET_LABELS[row.targetType]}</div>}
                <div className="min-w-0"><Label>Importe total imputado</Label><Input className="w-full min-w-0" type="number" min="0" step="0.01" value={row.allocatedAmount} onChange={(e) => setSetRows((rows) => rows.map((r, i) => i === index ? { ...r, allocatedAmount: e.target.value } : r))} /></div>
                <Button className="w-full sm:w-auto sm:justify-self-end" type="button" variant="outline" aria-label={`Quitar destino ${index + 1}`} onClick={() => setSetRows((rows) => rows.filter((_, i) => i !== index))}>Quitar</Button>
              </div>
            ))}
          </div>
          <div className="text-sm">Suma: {formatEUR(formatCentsAsDecimal(editedTotalCents))} / {formatEUR(totalAmount)}</div>
          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={() => setSetRows((rows) => [...rows, { targetType: "installation", targetId: "", allocatedAmount: "0.00", notes: "" }])}>Añadir obra</Button>
            <Button className="w-full sm:w-auto" type="button" disabled={replaceAllocations.isPending || (setRows.length > 0 && editedTotalCents !== invoiceTotalCents) || setRows.some((r) => r.targetType === "installation" && !r.targetId)} onClick={() => replaceAllocations.mutate({ invoiceId, allocations: setRows.map((r) => ({ targetType: r.targetType, targetId: r.targetType === "installation" ? Number(r.targetId) : null, allocatedAmount: normalizeAllocationAmount(r.allocatedAmount), allocatedPercentage: totalAmount ? (Number(r.allocatedAmount) / Number(totalAmount) * 100).toFixed(4) : null, notes: r.notes || null })) })}>Guardar reparto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId !== null ? "Editar imputación" : "Añadir imputación"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Tipo de destino *</Label>
              <Select
                value={form.targetType}
                onValueChange={(v) => setForm((p) => ({ ...p, targetType: v as SupplierInvoiceAllocationTarget, targetId: "" }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPLIER_INVOICE_ALLOCATION_TARGETS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {TARGET_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {needsId && (
              <div className="space-y-1.5">
                <Label>Obra / expediente *</Label>
                <Popover open={installationComboOpen} onOpenChange={setInstallationComboOpen}>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" role="combobox" className="w-full justify-between font-normal text-left truncate">
                      <span className="truncate">
                        {selectedInstallation
                          ? formatInstallationReference(selectedInstallation)
                          : installationsQuery.isLoading
                          ? "Cargando..."
                          : "Selecciona obra o expediente"}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-0 max-w-[calc(100vw-1.25rem)] overflow-hidden p-0" align="start">
                    <Command className="min-w-0 w-full max-w-full" filter={(value, search) => (installationMatchesSearchQuery((installationsQuery.data ?? []).find((i: any) => String(i.id) === value) ?? {}, search) ? 1 : 0)}>
                      <CommandInput placeholder="Buscar por expediente, cliente, sede, trabajo, dirección..." />
                      <CommandList>
                        <CommandEmpty>Sin expedientes encontrados.</CommandEmpty>
                        <CommandGroup>
                          {(installationsQuery.data ?? []).map((inst: any) => (
                            <CommandItem
                              key={inst.id}
                              value={String(inst.id)}
                              onSelect={() => {
                                setForm((p) => ({ ...p, targetId: String(inst.id) }));
                                setInstallationComboOpen(false);
                              }}
                            >
                              <span
                                className="block min-w-0 truncate text-sm"
                                title={formatInstallationReference({
                                  id: inst.id,
                                  installationCode: inst.installationCode,
                                  expedienteCode: inst.installationCode,
                                  clientId: inst.clientId,
                                  clientName: inst.clientName,
                                  siteName: inst.siteName,
                                  workDescription: inst.workDescription,
                                  name: inst.name,
                                  address: inst.address,
                                })}
                              >
                                {formatInstallationReference({
                                  id: inst.id,
                                  installationCode: inst.installationCode,
                                  expedienteCode: inst.installationCode,
                                  clientId: inst.clientId,
                                  clientName: inst.clientName,
                                  siteName: inst.siteName,
                                  workDescription: inst.workDescription,
                                  name: inst.name,
                                  address: inst.address,
                                })}
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="alloc-amount">Importe *</Label>
                <Input
                  id="alloc-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.allocatedAmount}
                  onChange={(e) => setForm((p) => ({ ...p, allocatedAmount: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="alloc-pct">Porcentaje</Label>
                <Input
                  id="alloc-pct"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.allocatedPercentage}
                  onChange={(e) => setForm((p) => ({ ...p, allocatedPercentage: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="alloc-notes">Notas</Label>
              <Textarea
                id="alloc-notes"
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Guardando..." : editingId !== null ? "Actualizar" : "Añadir"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
